import { useMemo } from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { SectionHead, FieldError } from "@/components/inquiry/inquiryFields";
import { CUSTOM_SERVICE_GROUPS } from "@/components/inquiry/inquiryServices";

/**
 * StepServices — grouped multi-select for the Custom Project Inquiry.
 * Uses `data.services_needed` (array of ids). At least one selection required.
 */
export const StepServices = ({ data, onToggleService, errors }) => {
    const selected = data.services_needed || [];
    const count = selected.length;

    const groups = useMemo(() => CUSTOM_SERVICE_GROUPS, []);

    return (
        <section data-testid="step-services">
            <SectionHead
                eyebrow="Step 02 · Services"
                title="Which design services do you need?"
                description="Pick everything that applies — you can add more or refine scope later. We’ll shape a Custom Engagement proposal from these choices."
            />

            <div
                className="mb-6 inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[11px] font-medium tracking-normal"
                style={{
                    borderColor: count > 0 ? "var(--pg-accent)" : "var(--pg-border)",
                    color: count > 0 ? "var(--pg-accent)" : "var(--pg-fg-muted)",
                    background: count > 0 ? "color-mix(in srgb, var(--pg-accent) 8%, transparent)" : "var(--pg-surface)",
                }}
                data-testid="services-selected-count"
            >
                {count === 0 ? "No services selected yet" : `${count} service${count === 1 ? "" : "s"} selected`}
            </div>

            <div className="space-y-10">
                {groups.map((group, gIdx) => (
                    <motion.div
                        key={group.id}
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.5, delay: 0.05 * gIdx, ease: [0.16, 1, 0.3, 1] }}
                        data-testid={`services-group-${group.id}`}
                    >
                        <div className="mb-4">
                            <h3 className="font-display text-lg" style={{ color: "var(--pg-fg)" }}>{group.title}</h3>
                            <p className="mt-1 text-xs" style={{ color: "var(--pg-fg-muted)" }}>{group.subtitle}</p>
                        </div>

                        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                            {group.services.map((s) => {
                                const active = selected.includes(s.id);
                                return (
                                    <label
                                        key={s.id}
                                        htmlFor={`svc-${s.id}`}
                                        data-testid={`services-item-${s.id}`}
                                        className="flex cursor-pointer items-center gap-3 rounded-2xl border p-3.5 transition-all"
                                        style={{
                                            borderColor: active ? "var(--pg-accent)" : "var(--pg-border)",
                                            background: active
                                                ? "color-mix(in srgb, var(--pg-accent) 8%, transparent)"
                                                : "var(--pg-surface)",
                                        }}
                                    >
                                        <Checkbox
                                            id={`svc-${s.id}`}
                                            checked={active}
                                            onCheckedChange={() => onToggleService(s.id)}
                                        />
                                        <span className="font-display text-sm" style={{ color: "var(--pg-fg)" }}>
                                            {s.label}
                                        </span>
                                        {active && (
                                            <Check className="ml-auto h-3.5 w-3.5" strokeWidth={2.4} style={{ color: "var(--pg-accent)" }} />
                                        )}
                                    </label>
                                );
                            })}
                        </div>
                    </motion.div>
                ))}
            </div>

            {errors?._step && (
                <FieldError error={errors._step} />
            )}
            <p className="mt-6 text-[11px]" style={{ color: "var(--pg-fg-muted)" }}>
                Not sure yet? Pick your best guess — we’ll refine the scope together in the proposal.
            </p>
        </section>
    );
};

export default StepServices;
